import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import DocumentTitle from 'react-document-title';
import {
	Alert,
	Button,
	ButtonGroup,
	Card,
	CardBody,
	CardHeader,
	Col,
	Container,
	Form,
	Input,
	Label,
	Row,
	Progress,
	UncontrolledTooltip
} from 'reactstrap';
import { Line, Chart } from 'react-chartjs-2';
import 'chartjs-plugin-annotation';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import ReactGA from 'react-ga';
import Select from 'react-select';

import {
	fetchNiNewestDate,
	resetNiNewestDate,
	fetchNiSmelterCapacity,
	resetNiSmelterCapacity,
	fetchNiPseudoGroups
} from 'actions';
import { cChartOptions, genChartStyle, plugins } from './chartConfig';
import { Crumbs } from 'containers';
import { DateRangePopover } from 'components';
import { multiSelectStyle } from './multiSelectStyle';
import { smeltersCapacityInlineHelpContent } from './smeltersCapacityInlineHelpContent';

import './Smelters.scss';

const config = {
	dateRangeDayLimit: 10,
	apiDateFormat: 'YYYYMMDD',
	dspDateFormat: 'YYYY-MM-DD',
	globalRegion: {
		name: 'Capacity',
		colour: '#FFA500',
		title: 'Global'
	},
	combinedRegion: {
		name: 'Combined capacity',
		colour: '#FFA500',
		title: 'Combine multiple regions'
	},
	fakePseudoGroups: [
		{
			name: 'Indonesia',
			key: 'indonesia'
		},
		{
			name: 'China',
			key: 'china'
		},
		{
			name: 'Asia and Oceania',
			key: 'asiaoceania'
		},
		{
			name: 'Europe',
			key: 'europe'
		},
		{
			name: 'Africa',
			key: 'africa'
		},

		{
			name: 'Americas',
			key: 'americas'
		}
	]
};

class NiSmeltersCapacity extends Component {
	state = {
		groupType: 'default',
		global: true,
		regions: [],
		gradeRegions: [],
		niTypeRegions: [],
		countryRegions: [],
		selectedRegions: [],
		compareCombine: '',
		dateMin: moment.utc('2016-03-01'),
		dateMax: moment.utc(),
		dateFrom: moment.utc(),
		dateTo: moment.utc(),
		isInitDateRange: false,
		shortDateRange: false,
		showDataWarning: false
	};

	async componentDidMount() {
		plugins.forEach((plugin) => {
			//register plugin if it is not already registered
			if (!Chart.pluginService._plugins.includes(plugin)) {
				Chart.pluginService.register(plugin);
			}
		});

		this.props.fetchNiPseudoGroups({ ute: false });
		this.props.fetchNiNewestDate();
		ReactGA.pageview('/ni/smelters', null, 'Ni Smelter Operations');
	}

	componentDidUpdate(
		{ niNewestDate: prevNiNewestDate },
		{
			isInitDateRange: prevIsInitDateRange,
			selectedRegions: prevSelectedRegions,
			global: prevGlobal,
			dateFrom: prevDateFrom,
			dateTo: prevDateTo
		}
	) {
		const { niNewestDate } = this.props;
		const { isInitDateRange, selectedRegions, global, dateFrom, dateTo } =
			this.state;

		//fetchNiNewestDate requested in ComponentDidMount(), ensure newestDate is received
		if (niNewestDate === '') {
			return;
		}

		//Date setting
		if (niNewestDate !== prevNiNewestDate) {
			this.updateDateHandling({ niNewestDate });
		}

		//once date range is set, fetch data
		if (isInitDateRange !== prevIsInitDateRange) {
			this.props.fetchNiSmelterCapacity({
				regions: 'global',
				dateFrom: dateFrom.format(config.apiDateFormat),
				dateTo: dateTo.format(config.apiDateFormat)
			});
		}

		if (
			((!global && global !== prevGlobal) || prevSelectedRegions.length > 0) &&
			selectedRegions.length === 0
		) {
			this.props.resetNiSmelterCapacity();
		}
		if (
			(global && global !== prevGlobal) ||
			(selectedRegions.length &&
				prevSelectedRegions.length !== selectedRegions.length) ||
			prevDateFrom !== dateFrom ||
			prevDateTo !== dateTo
		) {
			this.props.fetchNiSmelterCapacity({
				regions: global ? 'global' : selectedRegions.toString(),
				dateFrom: dateFrom.format(config.apiDateFormat),
				dateTo: dateTo.format(config.apiDateFormat)
			});
		}
	}

	componentWillUnmount() {
		this.props.resetNiSmelterCapacity();
		this.props.resetNiNewestDate();
	}

	async updateDateHandling({ niNewestDate }) {
		const maxDate = moment.utc(niNewestDate);

		//for enterprise user, savant-premium-plus, savant-premium
		let dateState = {
			dateFrom: maxDate.clone().subtract(2, 'y').subtract(1, 'd'),
			dateTo: maxDate.clone().subtract(1, 'd'),
			dateMax: maxDate.clone().subtract(1, 'd')
		};

		this.setState({
			...dateState,
			isInitDateRange: true
		});
	}

	// Radio button click for combine/compare
	async onRadioBtnClick(rSelected) {
		this.setState({ ...rSelected });
	}

	// Global button click
	async onGlobalBtnClick() {
		const { global, groupType } = this.state;
		let newState = {};
		//reset regions when switching to global, but only for current view
		if (!global) {
			if (groupType === 'default') {
				newState = { regions: [] };
			} else if (groupType === 'grade') {
				newState = { gradeRegions: [] };
			} else if (groupType === 'ni_type') {
				newState = { niTypeRegions: [] };
			} else if (groupType === 'country') {
				newState = { countryRegions: [] };
			}
			newState = { ...newState, selectedRegions: [] };
		}

		//toggle global
		newState = { ...newState, global: !global };
		this.setState(newState);
	}

	// region buttons
	async onRegionBtnClick({ prop, val }) {
		//existing regions
		let regions = this.state[prop];
		const oldRegionsCount = regions.length;

		if (regions.includes(val)) {
			regions = regions.filter((item) => item !== val);
		} else {
			regions = [...regions, val];
		}
		const sortedRegions = this.sortRegions(regions);
		let newState = {
			[prop]: sortedRegions,
			selectedRegions: sortedRegions,
			global: false
		};

		if (sortedRegions.length <= 1) {
			newState = { ...newState, compareCombine: '' };
		} else if (oldRegionsCount <= 1) {
			//regions count increased
			newState = { ...newState, compareCombine: 'combine' };
		}
		this.setState(newState);
	}

	async onSelectRegions(newRegions) {
		const { groupType, gradeRegions, niTypeRegions, countryRegions } =
			this.state;

		if (!newRegions) {
			newRegions = [];
		}

		let oldRegionsCount = 0;
		let newState = {};

		if (groupType === 'grade') {
			oldRegionsCount = gradeRegions.length;
			newState = { ...newState, gradeRegions: this.sortRegions(newRegions) };
		} else if (groupType === 'ni_type') {
			oldRegionsCount = niTypeRegions.length;
			newState = { ...newState, niTypeRegions: this.sortRegions(newRegions) };
		} else if (groupType === 'country') {
			oldRegionsCount = countryRegions.length;
			newState = { ...newState, countryRegions: this.sortRegions(newRegions) };
		}

		const sortedRegions = this.sortRegions(
			newRegions.map((region) => region.value)
		);

		newState = {
			...newState,
			global: false,
			selectedRegions: sortedRegions
		};

		if (newRegions.length <= 1) {
			newState = { ...newState, compareCombine: '' };
		} else if (oldRegionsCount <= 1) {
			//regions count increased
			newState = { ...newState, compareCombine: 'combine' };
		}
		this.setState(newState);
	}

	// Region toggling for the select all / none button
	async onToggleAllRegions() {
		const { regions, compareCombine } = this.state;
		const { niPseudoGroups } = this.props;
		const defaultGroups = niPseudoGroups.filter((g) => g.type === 'default');

		const newRegions =
			regions.length < defaultGroups.length
				? defaultGroups.map((g) => g.key)
				: [];
		const newCompareCombine = regions.length > 1 ? compareCombine : 'combine';
		this.setState({
			regions: newRegions,
			selectedRegions: newRegions,
			compareCombine: newRegions.length ? newCompareCombine : '',
			global: false
		});
	}

	// Handle date range selection event
	async onDateRangeSelect({ startDate, endDate }) {
		//startDate, endDate - moment objects
		this.setState({
			dateFrom: startDate,
			dateTo: endDate,
			shortDateRange: endDate.diff(startDate, 'days') < config.dateRangeDayLimit
		});
	}

	//sort plain array of regions' names or array of region objects
	sortRegions = (regions) => {
		const { niPseudoGroups } = this.props;
		const { groupType } = this.state;
		if (!regions.length) {
			return regions;
		}
		const regionOrder = niPseudoGroups
			.filter((g) => g.type === groupType)
			.map((g) => g.key);

		return regionOrder.reduce((acc, r) => {
			let filtered = [];
			filtered = regions.filter((elem) => {
				return Object.keys(elem).length && Object.keys(elem).includes('value')
					? elem['value'] === r
					: elem === r;
			});

			if (filtered.length) {
				acc.push(filtered[0]);
			}
			return acc;
		}, []);
	};

	onInputSelect = async ({ target: { value, name } }) => {
		const { regions, gradeRegions, niTypeRegions, countryRegions } = this.state;

		this.setState({
			[name]: value
		});
		if (name === 'groupType') {
			let selectedRegions = [];

			if (value === 'default') {
				selectedRegions = [...selectedRegions, ...regions];
			} else if (value === 'grade') {
				selectedRegions = [
					...selectedRegions,
					...gradeRegions.map((region) => region.value)
				];
			} else if (value === 'ni_type') {
				selectedRegions = [
					...selectedRegions,
					...niTypeRegions.map((region) => region.value)
				];
			} else if (value === 'country') {
				selectedRegions = [
					...selectedRegions,
					...countryRegions.map((region) => region.value)
				];
			}
			const global = selectedRegions.length ? false : true;
			this.setState({
				selectedRegions: selectedRegions,
				global: global
			});
		}
	};

	getChartType() {
		const { dateFrom, dateTo } = this.state;
		const days = dateTo.diff(dateFrom, 'd');
		const type = 'f';
		let size = '';

		if (days <= 50) {
			size = 'sm';
		} else if (days > 50 && days <= 100) {
			size = 'md';
		} else if (days > 100 && days <= 200) {
			size = 'lg';
		} else if (days > 200) {
			size = 'xl';
		} else if (days > 300) {
			size = 'xxl';
		}
		return { size, type };
	}

	// Build the card header element for display
	buildCardHeader() {
		const { compareCombine, global } = this.state;

		let title = 'Capacity:';
		if (global) {
			title = `${title} ${config.globalRegion.title}`;
		} else if (compareCombine === 'combine') {
			title = `${title} ${config.combinedRegion.title}`;
		} else if (compareCombine === 'compare') {
			title = `${title} Compare multiple regions`;
		} else {
			title = `${title} One region`;
		}

		return (
			<CardHeader>
				<i className="fa fa-line-chart" aria-hidden="true" /> {title}
			</CardHeader>
		);
	}

	buildRegionButton({ id, name, title, tooltip = '', disabled = false }) {
		const { regions } = this.state;

		return (
			<Fragment key={`region${name}`}>
				<Button
					id={id}
					outline
					size="sm"
					color="secondary"
					onClick={() => this.onRegionBtnClick({ prop: 'regions', val: name })}
					active={regions.includes(name)}
					aria-label={title}
					className="light-active-border"
					disabled={disabled}
				>
					{title}
				</Button>
				{tooltip !== '' && (
					<UncontrolledTooltip
						placement="top"
						delay={{ show: 1500, hide: 500 }}
						target={id}
						dangerouslySetInnerHTML={{ __html: tooltip }}
					/>
				)}
			</Fragment>
		);
	}

	buildGradeButton({ id, name, title, tooltip = '', disabled = false }) {
		const { gradeRegions } = this.state;

		return (
			<Fragment key={`grade${name}`}>
				<Button
					id={id}
					outline
					size="sm"
					color="secondary"
					onClick={() =>
						this.onRegionBtnClick({ prop: 'gradeRegions', val: name })
					}
					active={gradeRegions.includes(name)}
					aria-label={title}
					className="light-active-border"
					disabled={disabled}
				>
					{title}
				</Button>
				{tooltip !== '' && (
					<UncontrolledTooltip
						placement="top"
						delay={{ show: 1500, hide: 500 }}
						target={id}
						dangerouslySetInnerHTML={{ __html: tooltip }}
					/>
				)}
			</Fragment>
		);
	}

	buildNiTypeButton({ id, name, title, tooltip = '', disabled = false }) {
		const { niTypeRegions } = this.state;

		return (
			<Fragment key={`ni_type${name}`}>
				<Button
					id={id}
					outline
					size="sm"
					color="secondary"
					onClick={() =>
						this.onRegionBtnClick({ prop: 'niTypeRegions', val: name })
					}
					active={niTypeRegions.includes(name)}
					aria-label={title}
					className="light-active-border"
					disabled={disabled}
				>
					{title}
				</Button>
				{tooltip !== '' && (
					<UncontrolledTooltip
						placement="top"
						delay={{ show: 1500, hide: 500 }}
						target={id}
						dangerouslySetInnerHTML={{ __html: tooltip }}
					/>
				)}
			</Fragment>
		);
	}
	buildCsvDownloadButton() {
		const {
			global,
			shortDateRange,
			regions,
			gradeRegions,
			niTypeRegions,
			countryRegions,
			groupType
		} = this.state;
		const { niSmelterCapacity } = this.props;

		if (
			(global ||
				(groupType === 'default' && regions.length > 0) ||
				(groupType === 'grade' && gradeRegions.length > 0) ||
				(groupType === 'ni_type' && niTypeRegions.length > 0) ||
				(groupType === 'country' && countryRegions.length > 0)) &&
			!shortDateRange &&
			niSmelterCapacity &&
			niSmelterCapacity.length !== 0
		) {
			let { csvHeaders, csvData, csvFileName } = this.getCsvDataFromProps();
			return (
				<CSVLink
					id="buttonDownload"
					filename={csvFileName}
					headers={csvHeaders}
					data={csvData}
					className="btn btn-success mb-3 pull-right btn-sm"
					onClick={() => {
						ReactGA.event({
							category: 'niSmelterCapacity',
							action: 'data downloaded',
							label: csvFileName
						});
						return true;
					}}
				>
					<i className="fa fa-download mr-2" aria-hidden="true" /> Download this
					data
				</CSVLink>
			);
		}

		return (
			<Button
				id="buttonDownload"
				color="success"
				disabled
				className="mb-3 pull-right"
				size="sm"
			>
				<i className="fa fa-download mr-2" aria-hidden="true" /> Download this
				data
			</Button>
		);
	}

	// Build the filter form
	buildFilterForm() {
		const {
			niPseudoGroups = [],
			hasAdvancedGroups = false,
			gradeGroups,
			niTypeGroups,
			countryGroups,
			niNewestDate
		} = this.props;
		const {
			dateMin,
			dateMax,
			dateFrom,
			dateTo,
			compareCombine,
			global,
			groupType,
			regions,
			countryRegions,
			selectedRegions
		} = this.state;

		const regionToggleHidden = niPseudoGroups.length <= 2;
		const regionAllCopy =
			regions.length ===
			niPseudoGroups.filter(({ type }) => type === 'default').length
				? 'None'
				: 'All';
		const orderedKeys = config.fakePseudoGroups.map(({ key }) => key);
		return (
			<Form>
				<Container fluid className="px-0 container-width">
					<Row noGutters>
						<Col xs="12" sm="12">
							{hasAdvancedGroups && (
								<Label for="groupType" className="mb-2 mr-3 align-top">
									<span className="sr-only">Geographical filtering type</span>
									<Input
										id="groupType"
										name="groupType"
										type="select"
										bsSize="sm"
										className="pointered"
										defaultValue={groupType}
										onChange={this.onInputSelect}
									>
										<option value="default">Region filtering</option>
										<option value="grade">Metal grade filtering</option>
										<option value="ni_type">CN/ID Nickel type</option>
										<option value="country">Country filtering</option>
									</Input>
								</Label>
							)}

							<Button
								id="regionGlobal"
								outline
								size="sm"
								color="secondary"
								active={global}
								aria-label="global"
								className="mb-2 light-active-border"
								onClick={() => this.onGlobalBtnClick()}
							>
								Global
							</Button>

							{groupType === 'default' && niPseudoGroups.length === 0 && (
								<ButtonGroup className="mb-2 ml-3">
									{config.fakePseudoGroups.map(({ name, key }) => {
										return this.buildRegionButton({
											id: `region${key}`,
											name: key,
											title: name,
											disabled: true
										});
									})}
								</ButtonGroup>
							)}
							{groupType === 'default' && niPseudoGroups.length > 0 && (
								<ButtonGroup className="mb-2 ml-3">
									{niPseudoGroups
										.filter(({ type }) => type === 'default')
										.sort(
											(a, b) =>
												orderedKeys.indexOf(a.key) - orderedKeys.indexOf(b.key)
										)
										.map(({ name, key, tooltip = '' }) => {
											return this.buildRegionButton({
												id: `region${key}`,
												name: key,
												title: name,
												tooltip
											});
										})}
								</ButtonGroup>
							)}

							{groupType === 'default' && (
								<Button
									id="regionAll"
									color="primary"
									size="sm"
									onClick={() => this.onToggleAllRegions()}
									className="mb-2 ml-1"
									style={{ width: '54px' }}
									disabled={global}
									hidden={regionToggleHidden}
								>
									{regionAllCopy}
								</Button>
							)}
							{groupType === 'grade' && (
								<ButtonGroup className="mb-2 ml-3">
									{gradeGroups.map(({ label, value, tooltip = '' }) => {
										return this.buildGradeButton({
											id: `grade${value}`,
											name: value,
											title: label,
											tooltip
										});
									})}
								</ButtonGroup>
							)}
							{groupType === 'ni_type' && (
								<ButtonGroup className="mb-2 ml-3">
									{niTypeGroups.map(({ label, value, tooltip = '' }) => {
										return this.buildNiTypeButton({
											id: `ni_type${value}`,
											name: value,
											title: label,
											tooltip
										});
									})}
								</ButtonGroup>
							)}
							{groupType === 'country' && (
								<Select
									isMulti
									options={countryGroups}
									value={countryRegions}
									onChange={(val) => this.onSelectRegions(val)}
									className="react-select-container d-inline-block mb-2 ml-3"
									classNamePrefix="react-select"
									placeholder="Select countries..."
									styles={multiSelectStyle}
								/>
							)}
						</Col>
					</Row>
					<Row noGutters>
						<Col xs="12" sm="12">
							<DateRangePopover
								id="dateSelector"
								startDate={dateFrom}
								endDate={dateTo}
								minDate={dateMin}
								maxDate={dateMax}
								newestDate={niNewestDate}
								onDateRangeSelected={this.onDateRangeSelect.bind(this)}
							/>
							<ButtonGroup className="mb-3 mr-3 align-bottom">
								<Button
									id="buttonCombine"
									outline
									size="sm"
									color="secondary"
									onClick={() =>
										this.onRadioBtnClick({ compareCombine: 'combine' })
									}
									active={compareCombine === 'combine'}
									disabled={selectedRegions.length <= 1}
									aria-label="Click to combine smelter data"
								>
									Combine
								</Button>
								<Button
									id="buttonCompare"
									outline
									size="sm"
									color="secondary"
									onClick={() =>
										this.onRadioBtnClick({ compareCombine: 'compare' })
									}
									active={compareCombine === 'compare'}
									disabled={selectedRegions.length <= 1}
									aria-label="Click to compare smelter data"
								>
									Compare
								</Button>
							</ButtonGroup>
							{this.buildCsvDownloadButton()}
						</Col>
					</Row>
				</Container>
			</Form>
		);
	}

	// Build csv data from the props for the download feature
	getCsvDataFromProps() {
		const {
			niSmelterCapacity: { compare = [], combine = [] },
			niPseudoGroups
		} = this.props;
		let { dateFrom, dateTo, selectedRegions } = this.state;

		// Initialise our csv data
		let csvFileName = `ni-smelter-capacity-${dateFrom.format(
			config.apiDateFormat
		)}-${dateTo.format(config.apiDateFormat)}.csv`;
		let csvHeaders = [{ label: 'Date', key: 't' }];
		let csvData = [];

		const capacityData =
			selectedRegions.length === 0
				? [{ key: 'global', data: [...combine] }]
				: selectedRegions.length === 1
					? [{ key: selectedRegions[0], data: [...combine] }]
					: compare;

		capacityData.forEach(({ key, data }, i) => {
			const name =
				key !== 'global'
					? niPseudoGroups.find((pg) => pg['key'] === key)['name']
					: 'Global';
			data.forEach(({ t, c }, j) => {
				// Add the time column value to the csv data if this is the first region iteration
				// or if we've somehow got a frame error from the api data and the row is missing
				if (i === 0 || !csvData[j]) {
					csvData[j] = {
						t: moment.utc(t).format(config.dspDateFormat)
					};
				}

				// Generate the region-based capacity column names
				let nameckey = `${name} Capacity (kt)`;

				// Add the column names to the csv headers if this is the first data iteration
				if (j === 0) {
					csvHeaders = [
						...csvHeaders,
						...[
							{
								label: nameckey,
								key: nameckey
							}
						]
					];
				}

				// Set the capacity column on the csv data
				csvData[j][nameckey] = c;
			});
		});

		if (compare.length > 1) {
			combine.forEach(({ c }, k) => {
				// Generate the f column names
				let nameckey = `Combined Capacity (kt)`;

				// Add the column names to the csv headers if this is the first data iteration
				if (k === 0) {
					csvHeaders = [
						...csvHeaders,
						...[
							{
								label: nameckey,
								key: nameckey
							}
						]
					];
				}

				// Set the capacity column on the csv data
				csvData[k][nameckey] = c;
			});
		}

		return {
			csvFileName,
			csvHeaders,
			csvData
		};
	}

	buildRegionSelectError() {
		return (
			<Alert color="warning" className="p-2 mb-1">
				Please select at least one region.
			</Alert>
		);
	}

	buildShortDateRangeError() {
		return (
			<Alert color="warning" className="p-2 mb-1">
				Please select a date range of at least 10 days.
			</Alert>
		);
	}

	buildLoadingMessage() {
		return (
			<Fragment>
				<div className="h6 mb-3">Chart data loading...</div>
				<Progress animated value="100" />
			</Fragment>
		);
	}

	getSelectedDetail(selected = []) {
		const { niPseudoGroups = [] } = this.props;

		if (selected.length === 1 && selected[0] === 'global') {
			return [{ key: 'global', ...config.globalRegion }];
		} else if (selected.length === 1 && selected[0] === 'combine') {
			return [{ key: 'combine', ...config.combinedRegion }];
		} else if (selected.length >= 1) {
			return selected.map((s) =>
				niPseudoGroups.find((group) => group.key === s)
			);
		}
	}

	getChartData(dataArr) {
		const details = this.getSelectedDetail(dataArr.map(({ key }) => key));
		// Build the datasets by iterating the allRegions array and reducing it with the smelterData compare field,
		// so that the original order of the regions is maintained
		return dataArr.map(({ key, data }) => {
			const detail = details.find((d) => d.key === key);
			//filled point for combine
			const fCombinePointBackgroundColour =
				key === 'combine' ? { pointBackgroundColor: detail.colour } : {};

			//fStyle will be added to existing active capacity style in configuration
			const fStyle = {
				label: detail.name,
				borderColor: detail.colour,
				...fCombinePointBackgroundColour
			};

			const addStyle = fStyle;

			return {
				data: data.map(({ t, c }) => {
					return {
						t: moment.utc(t).format(config.dspDateFormat),
						y: c
					};
				}),
				...genChartStyle(this.getChartType()),
				...addStyle
			};
		});
	}

	// Individual data lines shown for multiple regions
	buildLineChart() {
		const { niSmelterCapacity } = this.props;
		const { compareCombine, global, selectedRegions } = this.state;

		const isCombined =
			(compareCombine === 'combine' && !global && selectedRegions.length > 1) ||
			selectedRegions.length <= 1;

		if (Object.keys(niSmelterCapacity).length === 0) {
			return this.buildLoadingMessage();
		}

		const apiData = isCombined
			? [{ key: 'combine', data: niSmelterCapacity.combine }]
			: niSmelterCapacity.compare;

		const datasets = this.getChartData(apiData);

		return (
			<Card className="mb-3">
				{this.buildCardHeader()}
				<CardBody>
					<Container
						fluid
						className="pl-0 container-width"
						style={{ paddingRight: '21px' }}
					>
						<Row noGutters className="mt-0">
							<Col xs="12" sm="12">
								<Line data={{ datasets }} options={cChartOptions} />
							</Col>
						</Row>
					</Container>
				</CardBody>
			</Card>
		);
	}

	render() {
		let { global, shortDateRange, selectedRegions } = this.state;

		return (
			<DocumentTitle title="SAVANT | Nickel Smelter Capacity">
				<div className="content-wrapper">
					<Container fluid>
						{/* Help, crumbs and the filter form */}
						<div className="pull-right">
							{smeltersCapacityInlineHelpContent}
						</div>
						<Crumbs type="ni" path={[{ title: 'Smelter Capacity' }]} />
						{this.buildFilterForm()}

						{/* Error messages */}
						{shortDateRange && this.buildShortDateRangeError()}
						{!shortDateRange &&
							!global &&
							!selectedRegions.length &&
							this.buildRegionSelectError()}

						{/* Different chart types */}
						{!shortDateRange &&
							((!global && selectedRegions.length > 0) ||
								(global && !selectedRegions.length)) &&
							this.buildLineChart()}
					</Container>
				</div>
			</DocumentTitle>
		);
	}
}

const mapStateToProps = ({
	niNewestDate,
	niPseudoGroups,
	niSmelterCapacity
}) => {
	let gradeGroups = [];
	let niTypeGroups = [];
	let countryGroups = [];
	let hasAdvancedGroups = false;

	if (niPseudoGroups && niPseudoGroups.length > 0) {
		gradeGroups = niPseudoGroups
			.filter((grp) => {
				return grp.type === 'grade';
			})
			.map(({ key, name, tooltip = '' }) => {
				return {
					value: key,
					label: name,
					tooltip
				};
			})
			.sort((a, b) => (a.label > b.label ? 1 : -1));
		niTypeGroups = niPseudoGroups
			.filter((grp) => {
				return grp.type === 'ni_type';
			})
			.map(({ key, name, tooltip = '' }) => {
				return {
					value: key,
					label: name,
					tooltip
				};
			});
		countryGroups = niPseudoGroups
			.filter((grp) => {
				return grp.type === 'country';
			})
			.map(({ key, name }) => {
				return {
					value: key,
					label: name
				};
			})
			.sort((a, b) => (a.label > b.label ? 1 : -1));
		hasAdvancedGroups = countryGroups.length > 0 || gradeGroups.length > 0;
	}

	return {
		niNewestDate,
		niPseudoGroups,
		niSmelterCapacity,
		gradeGroups,
		niTypeGroups,
		countryGroups,
		hasAdvancedGroups
	};
};

const mapDispatchToProps = (dispatch) => ({
	fetchNiNewestDate: () => dispatch(fetchNiNewestDate()),
	resetNiNewestDate: () => dispatch(resetNiNewestDate()),
	fetchNiSmelterCapacity: (filterData) =>
		dispatch(fetchNiSmelterCapacity(filterData)),
	resetNiSmelterCapacity: () => dispatch(resetNiSmelterCapacity()),
	fetchNiPseudoGroups: (filterData) => dispatch(fetchNiPseudoGroups(filterData))
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(withRouter(NiSmeltersCapacity));
